import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Plus,
  Check,
  Copy,
  ArrowRight,
  MessageSquare,
  ArrowLeft,
  AlertCircle,
  RotateCw,
} from "lucide-react";
import { api } from "../api";
import { postChatMessageStream, TraceEvent } from "../chatStream";
import AskComposer, { AskLevel, useAskLevel } from "./AskComposer";
import { useResearchMode } from "./ResearchModePicker";
import { useToast } from "../ui/toast";
import { useSourcesPref } from "../ui/sourcesPref";
import AssistantMessage from "./AssistantMessage";
import {
  ExternalSourceCard,
  FigureSourceCard,
  TableSourceCard,
  TextSourceCard,
} from "./source-pane/ChatSourceCard";

interface SourceBase {
  document_id: string;
  document_title: string;
  score: number;
}

interface TextSource extends SourceBase {
  kind?: "text";
  chunk_id: string;
  ordinal: number;
  content: string;
}

interface FigureSource extends SourceBase {
  kind: "figure";
  fig_id: string;
  page: number;
  caption: string | null;
  // Bei external sources (cosmetics_facts): source_hash + slug, sonst null.
  source_hash?: string | null;
  external_source_slug?: string | null;
}

interface TableSource extends SourceBase {
  kind: "table";
  table_id: string;
  page: number;
  caption: string | null;
  summary: string | null;
  html_preview: string | null;
  source_hash?: string | null;
  external_source_slug?: string | null;
}

interface ExternalSource extends SourceBase {
  kind: "external";
  chunk_id: string;
  ordinal: number;
  content: string;
  external_source_slug: string | null;
}

type Source = TextSource | FigureSource | TableSource | ExternalSource;

function sourceKind(s: Source): "text" | "figure" | "table" | "external" {
  return (s.kind ?? "text") as "text" | "figure" | "table" | "external";
}

function sourceKey(s: Source, i: number): string {
  const k = sourceKind(s);
  if (k === "figure") return `fig:${(s as FigureSource).fig_id}:${i}`;
  if (k === "table") return `tbl:${(s as TableSource).table_id}:${i}`;
  if (k === "external") {
    const ex = s as ExternalSource;
    return `ext:${ex.external_source_slug ?? ""}:${ex.chunk_id}:${i}`;
  }
  return `txt:${(s as TextSource).chunk_id}:${i}`;
}

interface ChatRow {
  id: string;
  project_id: string | null;
  project_name: string | null;
  title: string;
  model_override: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview: string | null;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sources_json: Source[] | null;
  model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
  // D5/D6 Agent-Mode: Trace persistiert, beim Reload aus DB geladen.
  agent_trace_json?: TraceEvent[] | null;
  agent_cancel_requested_at?: string | null;
  // Streaming-Flag — nur waehrend SSE-Token-Stream true; nach onMeta/onDone false.
  // Wird im AssistantMessage-Renderer fuer Cursor-Effekt benutzt.
  streaming?: boolean;
  // D6: 'agent' bei Agent-Mode-Messages; sonst absent (1-shot).
  mode?: "agent" | null;
  // D6 Live-Stream nur — Label fuer Status-Strip ('Recherchiere…',
  // 'Erzeuge Praesentation…'). Persistiert nicht.
  liveStatus?: string | null;
}

interface ChatDetail {
  chat: ChatRow;
  messages: ChatMessage[];
}

// Level → Modell (Fallback, falls /settings noch nicht geantwortet hat).
// Identisch zu DIFFICULTY_DEFAULTS im Backend (app/llm/pricing.py); per-User
// wird über user_settings.payload.difficulty_models überschrieben. Der Level
// selbst liegt zentral in AskComposer (localStorage `sb_ask_level`).
/** Obergrenze der Chat-Liste auf der Projektseite. Der Endpunkt selbst hat
 *  keine (`app/routers/chats.py:153`: `limit` kommt ungeprüft vom Aufrufer), also
 *  muss die Seite deckeln — und den Deckel dann auch ZEIGEN, sonst fehlen ab
 *  hier lautlos Chats. */
const CHAT_LISTE_MAX = 200;

const LEVEL_MODELS: Record<AskLevel, string | null> = {
  easy: null,
  medium: "gemini-3-flash-preview",
  hard: "claude-sonnet-4-6",
  sophisticated: "claude-opus-4-7",
};

// D6: Trace-Event -> menschen-lesbares Live-Status-Label.
// Wird im Status-Strip in AssistantMessage gerendert.
function labelForTriggerStrict(trigger: string | null | undefined): string | null {
  if (!trigger) return null;
  const t = trigger.toLowerCase();
  if (t.startsWith("recherch")) return "Recherchiere …";
  if (t.startsWith("präsent") || t.startsWith("praesent") || t === "präsentation" || t === "praesentation") return "Erzeuge Praesentation …";
  return "Agent-Modus aktiv …";
}
export function labelForTrigger(trigger: string | null | undefined): string | null {
  return labelForTriggerStrict(trigger);
}

export function labelForTraceEvent(ev: TraceEvent): string | null {
  switch (ev.type) {
    case "trigger":
      return labelForTriggerStrict(ev.matched ?? null) ?? "Agent gestartet …";
    case "digest":
      return "Kontext-Digest erstellt …";
    case "plan":
      return ev.deliverable ? `Plan: ${ev.deliverable} …` : "Plan formuliert …";
    case "plan_error":
      return "Plan-Step fehlgeschlagen.";
    case "storyline":
      return ev.title ? `Storyline: ${ev.title} …` : "Storyline-Blueprint erstellt …";
    case "storyline_error":
      return "Storyline-Blueprint fehlgeschlagen.";
    case "initial_retrieve":
      return `Quellen abgefragt (${ev.hits ?? 0} Treffer) …`;
    case "thinking":
      return ev.iteration ? `Iteration ${ev.iteration} — analysiere Plan …` : "Plane …";
    case "reasoning":
      return "Plan formuliert …";
    case "tool_use":
      if (ev.tool === "retrieve_more") return "Suche zusaetzliche Quellen …";
      if (ev.tool === "web_research") return "Recherchiere im Web …";
      if (ev.tool === "create_presentation") return "Erzeuge Praesentation …";
      return `Tool laeuft: ${ev.tool ?? "?"}`;
    case "tool_result":
      if (ev.tool === "create_presentation" && ev.presentation_id) return "Praesentation fertig.";
      return null;
    case "final":
      return "Fertig.";
    case "cancelled":
      return "Abgebrochen.";
    case "llm_error":
    case "stream_error":
      return "Fehler im Agent-Loop.";
    default:
      return null;
  }
}

export default function ProjectChatView({
  projectId,
  projectName,
}: {
  projectId: string;
  projectName: string;
}) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Chat-Auswahl kommt jetzt aus der URL (Phase-2-Restructure, 2026-06-20).
  // ?chat=<uuid> = aktiver Chat. Sidebar-Tree linkt dort hin; ohne den Param
  // ist der "Neuer Chat"-Modus aktiv.
  const urlChatId = searchParams.get("chat");
  const [activeChatId, setActiveChatId] = useState<string | null>(urlChatId);
  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  // D6: Stop-Button-Hook — Ref hat (msgId) => abort + cancel-agent API call.
  // Pro Stream neu gesetzt, cleared bei done.
  const cancelStreamRef = useRef<((msgId: string) => void) | null>(null);
  const [level, setLevel] = useAskLevel();
  const [researchMode, setResearchMode] = useResearchMode();
  // Per-User-Override für den Level → Modell aus /settings.difficulty_models.
  // Solange noch nichts geladen: Fallback auf LEVEL_MODELS (gleiche Defaults wie
  // Backend), sodass der Chat auch ohne Settings-Fetch funktioniert.
  const [difficultyModels, setDifficultyModels] = useState<Record<string, string | null>>({});
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const activeModel: string | null =
    level in difficultyModels ? difficultyModels[level] : LEVEL_MODELS[level];

  // Lade Difficulty-Routing einmal pro Projekt-Wechsel (User-spezifisch,
  // ändert sich nur, wenn User die Settings-Seite besucht; einfacher
  // Re-Fetch beim Mounten reicht).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get<{ difficulty_models?: Record<string, string | null> }>(
          "/settings"
        );
        if (!cancelled && s.difficulty_models) {
          setDifficultyModels(s.difficulty_models);
        }
      } catch {
        /* still works via DIFFICULTY fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Beim Projekt-Wechsel: reset Composer + Focus.
  useEffect(() => {
    setQ("");
    setTimeout(() => composerRef.current?.focus(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Chat-Liste des Projekts. Sie lag von 2026-06-20 bis 2026-07-29 AUSSCHLIESSLICH
  // im Sidebar-Tree — und dort nur die fünf jüngsten. Mit dem Tree-Unterbaum ist
  // sie hierher zurückgekehrt, vollständig: die Projektseite ist der Ort, an dem
  // man die Chats eines Projekts sucht.
  const [chats, setChats] = useState<ChatRow[]>([]);
  // Zwei GETRENNTE Zustände, absichtlich: „keine Chats vorhanden" und „Liste
  // konnte nicht geladen werden" sehen sonst identisch aus (leere Liste), und
  // ein Ausfall verschwindet lautlos. `null` = kein Fehler.
  const [chatsFehler, setChatsFehler] = useState<string | null>(null);
  // Der Server deckelt nicht (app/routers/chats.py:153 nimmt jedes `limit`), also
  // deckelt die Seite — und sagt es dann auch. Es wird EINS mehr geholt als
  // gezeigt: nur so ist „genau 200" von „mehr als 200" unterscheidbar.
  const [chatsGekappt, setChatsGekappt] = useState(false);
  // `projectId` in einem Ref, damit `reloadChats` stabil bleibt und der Effect
  // unten nicht bei jedem Render neu feuert.
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const reloadChats = useCallback(async () => {
    const fuer = projectIdRef.current;
    try {
      const rows = await api.get<ChatRow[]>(
        `/chats?project_id=${encodeURIComponent(fuer)}&limit=${CHAT_LISTE_MAX + 1}`
      );
      // Wurde inzwischen das Projekt gewechselt, gehoert diese Antwort nicht
      // mehr hierher — sonst zeigt die Liste die Chats des vorigen Projekts.
      if (projectIdRef.current !== fuer) return;
      setChatsGekappt(rows.length > CHAT_LISTE_MAX);
      setChats(rows.slice(0, CHAT_LISTE_MAX));
      setChatsFehler(null);
    } catch (e) {
      // NICHT mehr verschlucken: ohne diese Zeile bleibt die Seite stehen, die
      // Liste ist weg und nichts sagt warum — ein Ausfall, der wie „dieses
      // Projekt hat keine Chats" aussieht.
      if (projectIdRef.current !== fuer) return;
      setChats([]);
      setChatsGekappt(false);
      setChatsFehler((e as Error)?.message || "unbekannter Fehler");
    }
  }, []);
  useEffect(() => {
    setChats([]);
    setChatsFehler(null);
    setChatsGekappt(false);
    void reloadChats();
  }, [projectId, reloadChats]);

  // URL → activeChatId: bei URL-Wechsel (z.B. Sidebar-Klick auf anderen
  // Chat) den lokalen State nachziehen, sonst zeigt die Komponente noch
  // den alten Chat.
  useEffect(() => {
    setActiveChatId(urlChatId);
    if (!urlChatId) {
      setDetail(null);
      setQ("");
    }
  }, [urlChatId]);

  // Wenn Chat aktiviert: Detail nachladen.
  // Race-fix (Bug 2026-06-19): nach erstem Submit in einem neuen Chat ruft
  // send() setActiveChatId(neueId). Dieser Effect feuerte dann einen GET, der
  // den gerade-erstellten leeren Chat zurückgab und das optimistische detail
  // (mit der User-Message) überschrieb → Inline-Composer + EmptyState
  // ploppten kurz wieder auf, bis das POST /messages antwortete. Skip den
  // GET, wenn detail bereits zum aktiven chat gehört — send() pflegt detail
  // selbst weiter.
  useEffect(() => {
    if (!activeChatId) {
      setDetail(null);
      return;
    }
    if (detail?.chat.id === activeChatId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<ChatDetail>(`/chats/${activeChatId}`);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) toast.err("Chat konnte nicht geladen werden", (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const newChat = () => {
    // URL → ohne ?chat=, dann fängt der urlChatId-Effect den Reset auf.
    setSearchParams({}, { replace: true });
    // Beim Zurückspringen in die Liste neu laden: wer gerade einen Chat
    // begonnen hat, muss ihn hier auch finden.
    void reloadChats();
    setTimeout(() => composerRef.current?.focus(), 50);
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text) return;

    let chatId = activeChatId;
    setBusy(true);
    setQ("");

    // Optimistisches Anhängen
    const tempMsg: ChatMessage = {
      id: `tmp_${Date.now()}`,
      chat_id: chatId ?? "",
      role: "user",
      content: text,
      sources_json: null,
      model: null,
      tokens_in: null,
      tokens_out: null,
      created_at: new Date().toISOString(),
    };
    if (detail) {
      setDetail({ ...detail, messages: [...detail.messages, tempMsg] });
    } else {
      // Noch kein Chat: rendere Optimistic-Detail mit nur dieser Message
      setDetail({
        chat: {
          id: "pending",
          project_id: projectId,
          project_name: projectName,
          title: "Neuer Chat",
          model_override: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          message_count: 1,
          last_message_preview: text,
        },
        messages: [tempMsg],
      });
    }

    try {
      if (!chatId) {
        const created = await api.post<ChatRow>("/chats", {
          project_id: projectId,
        });
        chatId = created.id;
        // chat.id im optimistischen detail synchronisieren BEVOR
        // setActiveChatId — sonst feuert der activeChatId-Effect einen GET
        // auf den frisch erstellten leeren Chat und überschreibt unser
        // optimistic detail mit messages=[] (Bug 4, 2026-06-19).
        setDetail((d) => (d ? { ...d, chat: { ...d.chat, id: chatId! } } : d));
        setActiveChatId(chatId);
        // URL nachziehen, damit Sidebar den neuen Chat als aktiv markiert.
        setSearchParams({ chat: chatId! }, { replace: true });
      }
      // SSE-Stream — Token-by-Token-Rendering (Atlas-Task a823c24a)
      // + D6 Agent-Mode Live-Trace (Atlas-Task fe4405c4)
      let assistantMsgId: string | null = null;
      let streamedSources: Source[] | null = null;
      let streamedContent = "";
      let streamedMeta: { model: string | null; tokens_in: number | null; tokens_out: number | null } = {
        model: null, tokens_in: null, tokens_out: null,
      };
      let streamError: string | null = null;

      // Replace tempMsg with the real user_msg id when it arrives, then
      // append a streaming assistant-bubble that we mutate per delta.
      const { done: streamDone, cancel: streamCancel } = postChatMessageStream(
        chatId!,
        { content: text, model: activeModel, top_k: 20, research_mode: researchMode },
        {
          onUserMsg: (m) => {
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) =>
                x.id === tempMsg.id
                  ? { ...x, id: m.id, chat_id: m.chat_id, created_at: m.created_at ?? x.created_at }
                  : x,
              );
              return { ...d, messages: msgs };
            });
          },
          onAssistantMsg: (m) => {
            assistantMsgId = m.id;
            setDetail((d) => {
              if (!d) return d;
              const assistantShell: ChatMessage = {
                id: m.id,
                chat_id: m.chat_id,
                role: "assistant",
                content: "",
                sources_json: null,
                model: null,
                tokens_in: null,
                tokens_out: null,
                created_at: new Date().toISOString(),
                streaming: true,
                mode: m.mode === "agent" ? "agent" : null,
                agent_trace_json: m.mode === "agent" ? [] : null,
                liveStatus: m.mode === "agent" ? labelForTrigger(m.trigger) : null,
              };
              return { ...d, messages: [...d.messages, assistantShell] };
            });
          },
          onTrace: (ev) => {
            if (!assistantMsgId) return;
            const label = labelForTraceEvent(ev);
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) => {
                if (x.id !== assistantMsgId) return x;
                const trace = [...(x.agent_trace_json ?? []), ev];
                return { ...x, agent_trace_json: trace, liveStatus: label ?? x.liveStatus ?? null };
              });
              return { ...d, messages: msgs };
            });
          },
          onSources: (srcs) => {
            streamedSources = srcs as Source[];
            if (!assistantMsgId) return;
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) =>
                x.id === assistantMsgId ? { ...x, sources_json: streamedSources } : x,
              );
              return { ...d, messages: msgs };
            });
          },
          onDelta: (delta) => {
            streamedContent += delta;
            if (!assistantMsgId) return;
            const snapshot = streamedContent;
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) =>
                x.id === assistantMsgId ? { ...x, content: snapshot } : x,
              );
              return { ...d, messages: msgs };
            });
          },
          onMeta: (meta) => {
            streamedMeta = {
              model: meta.model || null,
              tokens_in: meta.tokens_in || null,
              tokens_out: meta.tokens_out || null,
            };
            if (meta.error) streamError = meta.error;
            // D6 Agent-Pfad: content + sources_json kommen erst hier am
            // Ende (kein Token-Stream waehrend Tool-Loop). Wir applizieren
            // sie auf die Streaming-Shell.
            const agentContent = meta.content;
            const agentSources = meta.sources_json as Source[] | null | undefined;
            if (!assistantMsgId) return;
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) => {
                if (x.id !== assistantMsgId) return x;
                const next: ChatMessage = {
                  ...x,
                  ...streamedMeta,
                  streaming: false,
                  liveStatus: null,
                };
                if (agentContent != null) next.content = agentContent;
                if (agentSources !== undefined) next.sources_json = agentSources;
                return next;
              });
              return { ...d, messages: msgs };
            });
          },
          onDone: () => {
            // streaming-Flag final aus + Refresh aus DB nicht noetig
            if (!assistantMsgId) return;
            setDetail((d) => {
              if (!d) return d;
              const msgs = d.messages.map((x) =>
                x.id === assistantMsgId ? { ...x, streaming: false } : x,
              );
              return { ...d, messages: msgs };
            });
          },
          onError: (err) => {
            streamError = err.message;
            toast.err("Antwort fehlgeschlagen", err.message);
          },
        },
      );

      // D6: Stop-Button-Hook. Fuer Agent-Mode-Stream geben wir dem
      // Button-Click eine Funktion mit, die (a) den SSE-Stream abbricht,
      // (b) cancel-agent gegen das Backend feuert damit der Loop nach
      // dem naechsten Iter-Check ueber agent_cancel_requested_at sauber
      // austritt.
      cancelStreamRef.current = (msgId: string) => {
        streamCancel();
        if (chatId) {
          api.post(`/chats/${chatId}/messages/${msgId}/cancel-agent`).catch(() => {
            /* swallow — beste-effort */
          });
        }
      };

      await streamDone;
      cancelStreamRef.current = null;
      if (streamError) {
        // Fallback: kompletten Chat-State frisch ziehen
        try {
          setDetail(await api.get<ChatDetail>(`/chats/${chatId}`));
        } catch {
          /* swallow */
        }
      }
    } catch (err) {
      toast.err("Antwort fehlgeschlagen", (err as Error).message);
      if (chatId) {
        try {
          setDetail(await api.get<ChatDetail>(`/chats/${chatId}`));
        } catch {
          /* swallow */
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // Header-Titel: bei aktivem Chat dessen Titel, sonst „Neuer Chat".
  const headerChatTitle = detail?.chat.title ?? "Neuer Chat";

  return (
    <section className="flex flex-col min-h-0 h-full">
      {/* Header: Chat-Titel + Rücksprung in die Liste. Ohne den Rücksprung wäre
          ein geöffneter Chat eine Sackgasse — der Sidebar-Tree, über den man
          früher zum nächsten Chat kam, führt sie nicht mehr. */}
      <div className="px-5 py-3 border-b border-cream-200 dark:border-ink-600 flex items-center gap-2">
        {activeChatId && (
          <button
            onClick={newChat}
            data-chats-back
            className="shrink-0 inline-flex items-center gap-1 text-xs text-ink-50/70 hover:text-brand-600 transition-colors"
            title="Zurück zur Chat-Liste dieses Projekts"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Alle Chats
          </button>
        )}
        <div className="flex items-center gap-2 text-sm min-w-0">
          <Sparkles className="w-4 h-4 text-brand-500 shrink-0" />
          <span className="font-medium truncate">{headerChatTitle}</span>
          {!activeChatId && (
            <button
              onClick={newChat}
              className="ml-1 text-[11px] text-ink-50/50 hover:text-brand-600 inline-flex items-center gap-0.5"
              title="Neuen leeren Chat starten"
            >
              <Plus className="w-3 h-3" /> neu
            </button>
          )}
        </div>
      </div>

        {/* Messages — bei leerem Chat zeigt der Composer INLINE darunter */}
        <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
          {!detail || detail.messages.length === 0 ? (
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Die Chats des Projekts — VOLLSTÄNDIG, nicht die fünf jüngsten
                  wie früher im Sidebar-Tree. Nur ohne gewählten Chat: sonst
                  liest man einen Chat und hat die Liste darüber im Weg. */}
              {!activeChatId &&
                (chatsFehler ? (
                  <ProjectChatListFehler
                    grund={chatsFehler}
                    onRetry={() => void reloadChats()}
                  />
                ) : chats.length > 0 ? (
                  <ProjectChatList chats={chats} gekappt={chatsGekappt} />
                ) : null)}
              <ProjectEmptyState projectName={projectName} onPick={(s) => {
                setQ(s);
                setTimeout(() => composerRef.current?.focus(), 0);
              }} />
              <AskComposer
                q={q}
                setQ={setQ}
                onSend={send}
                busy={busy}
                composerRef={composerRef}
                level={level}
                setLevel={setLevel}
                researchMode={researchMode}
                setResearchMode={setResearchMode}
              />
            </div>
          ) : (
            detail.messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                onCancel={
                  m.streaming === true && m.mode === "agent"
                    ? () => cancelStreamRef.current?.(m.id)
                    : undefined
                }
              />
            ))
          )}
          {busy && !detail?.messages.some((m) => m.streaming) && (
            <div className="flex items-center gap-2 text-sm text-ink-50/60">
              <Loader2 className="w-4 h-4 animate-spin text-brand-500" /> denkt nach …
            </div>
          )}
          <div ref={bottomRef} />
        </div>

      {/* Composer als fixer Footer — nur wenn mindestens 1 Message existiert */}
      {detail && detail.messages.length > 0 && (
        <div className="px-5 pb-5 pt-3 border-t border-cream-200 dark:border-ink-600">
          <AskComposer
            q={q}
            setQ={setQ}
            onSend={send}
            busy={busy}
            composerRef={composerRef}
            level={level}
            setLevel={setLevel}
            researchMode={researchMode}
            setResearchMode={setResearchMode}
          />
        </div>
      )}
    </section>
  );
}

function fmtChatDate(iso: string) {
  const d = new Date(iso);
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 3600) return "gerade";
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 7) return `vor ${Math.floor(diffSec / 86400)} T`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

/** Die Chats dieses Projekts. Ersetzt den Unterbaum, der bis 2026-07-29 im
 *  Sidebar-Tree hing — dort 12px, eingerückt, abgeschnitten und auf 5 gekappt. */
function ProjectChatList({ chats, gekappt }: { chats: ChatRow[]; gekappt: boolean }) {
  return (
    <div data-project-chat-list>
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs uppercase tracking-wider text-ink-50/50">
          Chats in diesem Projekt
        </h3>
        {/* Beim Deckel „200+" statt „200": sonst liest sich die Zahl als
            Gesamtzahl, obwohl der Rest fehlt. */}
        <span className="text-xs tabular-nums text-ink-50/40">
          {gekappt ? `${CHAT_LISTE_MAX}+` : chats.length}
        </span>
      </div>
      {gekappt && (
        <p
          data-project-chat-cap
          className="mb-2 text-xs text-ink-50/60"
        >
          Es gibt mehr Chats in diesem Projekt, als hier stehen. Gezeigt werden die{" "}
          {CHAT_LISTE_MAX} zuletzt bearbeiteten.
        </p>
      )}
      <div className="rounded-xl border border-cream-200 dark:border-ink-600 divide-y divide-cream-200 dark:divide-ink-600 overflow-hidden">
        {chats.map((c) => (
          <Link
            key={c.id}
            // Nur die Query ändern — die Seite liegt je nach Einstiegspunkt
            // unter /p/<id> ODER /projects/<id>; ein absoluter Pfad würde einen
            // davon stillschweigend umschreiben.
            to={{ search: `?chat=${c.id}` }}
            data-project-chat={c.id}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-cream-50 dark:hover:bg-ink-800/60 transition-colors"
          >
            <MessageSquare className="w-4 h-4 shrink-0 text-ink-50/40" />
            <span className="flex-1 min-w-0 truncate text-ink">{c.title}</span>
            {c.message_count > 0 && (
              <span className="shrink-0 text-xs tabular-nums text-ink-50/40">
                {c.message_count}
              </span>
            )}
            <span className="shrink-0 text-xs text-ink-50/50 w-16 text-right">
              {fmtChatDate(c.updated_at)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Der Ausfall-Pfad der Chat-Liste. Er MUSS anders aussehen als „dieses Projekt
 *  hat noch keine Chats" — sonst ist ein kaputter Endpunkt von einem leeren
 *  Projekt nicht zu unterscheiden und der Ausfall verschwindet als Leerzustand.
 *  Deshalb: eigener Knoten (`data-project-chat-error`, NICHT
 *  `data-project-chat-list`), eigene Farbe, benannter Grund, ein Weg zurück. */
function ProjectChatListFehler({
  grund,
  onRetry,
}: {
  grund: string;
  onRetry: () => void;
}) {
  return (
    <div
      data-project-chat-error
      role="alert"
      // Text in `text-text`, NICHT in `text-danger`: die Signalfarbe traegt hier
      // der Rahmen, die Flaeche und das Symbol. Als Schrift auf `bg-danger-soft`
      // reicht sie nicht — gemessen 4.48:1 im hellen Modus, also unter der
      // AA-Schwelle von 4.5:1 fuer Fliesstext, und das schon OHNE Deckkraft.
      // `--text` ist der Token, der in diesem Haus auf AA geprueft ist
      // (themes.css:19/21). Rahmen und Symbol duerfen `danger` bleiben: fuer
      // Nicht-Text gilt 3:1 (WCAG 1.4.11), das haelt die Farbe locker.
      className="flex items-start gap-2 rounded-xl border border-danger/40 bg-danger-soft px-3 py-2.5 text-sm text-text"
    >
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-danger" />
      <div className="min-w-0 flex-1">
        <div className="font-medium">Chat-Liste konnte nicht geladen werden.</div>
        {/* KEIN `opacity-80`: `opacity` mischt die Schrift beim Rendern in den
            Grund und faellt in `color` nicht auf — der benannte Ausfallgrund lag
            damit bei 3.32:1 (hell) bzw. 3.68:1 (dunkel). Ausgerechnet die Zeile,
            die dem Nutzer sagt WARUM, war die am schlechtesten lesbare. Rang
            macht hier die Schriftgroesse, nicht die Deckkraft. */}
        <div className="mt-0.5 text-xs break-words">{grund}</div>
        <div className="mt-1 text-xs">
          Es kann also nicht gesagt werden, ob dieses Projekt Chats hat.
        </div>
      </div>
      <button
        type="button"
        data-project-chat-retry
        onClick={onRetry}
        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-danger/40 px-2 py-1 text-xs hover:bg-danger/10 transition-colors"
      >
        <RotateCw className="w-3 h-3" />
        Erneut versuchen
      </button>
    </div>
  );
}

function ProjectEmptyState({
  projectName,
  onPick,
}: {
  projectName: string;
  onPick: (q: string) => void;
}) {
  const suggestions = [
    "Was steht in der Wissensbasis zu diesem Projekt?",
    "Welche Aufgaben sind noch offen?",
    "Fasse die letzten Updates zusammen.",
    "Welche Dokumente sind hochgeladen?",
  ];
  return (
    <div className="max-w-2xl mx-auto py-12 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shadow-lg shadow-brand-500/20">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h2 className="font-display text-2xl text-ink tracking-tight">
          Frag <span className="italic text-brand-600 dark:text-brand-400">{projectName}</span>
        </h2>
        <p className="text-sm text-ink-50/70 max-w-md mx-auto">
          Wissen dieses Projekts wird automatisch als Kontext genutzt.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="group text-left px-4 py-3 rounded-xl bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-sm transition"
          >
            <div className="flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-brand-500 mt-0.5 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              <span className="text-[13.5px] text-ink leading-snug">
                {s}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg, onCancel }: { msg: ChatMessage; onCancel?: () => void }) {
  const { state: sourcesPref } = useSourcesPref();
  // Wrapper-Default abhaengig vom User-Pref: nur "collapsed" startet zu.
  const [openSources, setOpenSources] = useState(sourcesPref !== "collapsed");
  const autoOpenedRef = useRef(false);
  // Wenn der User explizit "collapsed" gewaehlt hat: KEIN auto-promote bei
  // Bild/Tabelle — User-Pref schlaegt heuristisch-promote. Sonst (titles/
  // expanded): top-Visual triggert das Aufklappen falls es noch zu ist.
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (sourcesPref === "collapsed") return;
    const srcs = msg.sources_json ?? [];
    const hasTopVisual = srcs.slice(0, 3).some(s => sourceKind(s) !== "text");
    if (hasTopVisual) {
      setOpenSources(true);
      autoOpenedRef.current = true;
    }
  }, [msg.sources_json, msg.id, sourcesPref]);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (msg.role === "user") {
    const u =
      (typeof localStorage !== "undefined" ? localStorage.getItem("sb_username") : null) ?? "?";
    return (
      <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/50 dark:hover:bg-ink-700/30 transition-colors">
        <div className="w-8 h-8 rounded-full bg-ink/85 dark:bg-cream-200 grid place-items-center text-cream-50 dark:text-ink text-xs font-semibold shrink-0">
          {u.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0 text-[15px] leading-relaxed whitespace-pre-wrap text-ink pt-0.5">
          {msg.content}
        </div>
        <button
          onClick={handleCopy}
          title={copied ? "Kopiert!" : "Kopieren"}
          className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }
  const sources = msg.sources_json ?? [];
  const [highlightSrc, setHighlightSrc] = useState<number | null>(null);
  const handleCite = (n: number) => {
    setOpenSources(true);
    setHighlightSrc(n);
    setTimeout(() => {
      const el = document.getElementById(`src-${msg.id}-${n}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const det = el as HTMLDetailsElement;
        if (det.tagName === "DETAILS") det.open = true;
      }
      setTimeout(() => setHighlightSrc(null), 2500);
    }, 50);
  };
  return (
    <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/30 dark:hover:bg-ink-700/20 transition-colors">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shrink-0 shadow-sm shadow-brand-500/20">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <AssistantMessage
          content={msg.content}
          onCite={sources.length > 0 ? handleCite : undefined}
          sources={sources}
          streaming={msg.streaming === true}
          mode={msg.mode ?? null}
          liveStatus={msg.liveStatus ?? null}
          agentTrace={msg.agent_trace_json ?? null}
          onCancel={msg.streaming === true && msg.mode === "agent" ? onCancel : undefined}
        />
        <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-50/50">
          {msg.tokens_in != null && (
            <span className="font-mono">
              ⚡ {msg.tokens_in}+{msg.tokens_out ?? 0} tok
            </span>
          )}
        </div>
        {sources.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setOpenSources((v) => !v)}
              className="text-xs text-ink-50/60 hover:text-brand-600 inline-flex items-center gap-1.5 transition-colors"
            >
              {openSources ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              <span>{sources.length} Quellen</span>
              {(() => {
                const figs = sources.filter(s => sourceKind(s) === "figure").length;
                const tbls = sources.filter(s => sourceKind(s) === "table").length;
                if (!figs && !tbls) return null;
                return (
                  <span className="text-ink-50/40">
                    ·{figs > 0 && <> {figs} Bild{figs === 1 ? "" : "er"}</>}
                    {figs > 0 && tbls > 0 && " ·"}
                    {tbls > 0 && <> {tbls} Tabelle{tbls === 1 ? "" : "n"}</>}
                  </span>
                );
              })()}
            </button>
            {openSources && (
              <div className="mt-2 space-y-2">
                {sources.map((s, i) => {
                  const n = i + 1;
                  const isHi = highlightSrc === n;
                  const k = sourceKind(s);
                  // User-Pref schlaegt die alte Auto-Promote-Heuristik:
                  //   expanded → alles offen
                  //   titles   → alles zu (User will Titel-Liste)
                  //   collapsed→ Cards sind initial irrelevant (Wrapper zu),
                  //              fuer Konsistenz aber auch zu lassen.
                  const defaultOpen =
                    sourcesPref === "expanded"
                      ? true
                      : sourcesPref === "titles" || sourcesPref === "collapsed"
                        ? false
                        : i < 3 && k !== "text";
                  const common = {
                    msgId: msg.id,
                    n,
                    isHi,
                    documentId: s.document_id,
                    documentTitle: s.document_title,
                    defaultOpen,
                  };
                  if (k === "figure") {
                    const f = s as FigureSource;
                    return (
                      <FigureSourceCard
                        key={sourceKey(s, i)}
                        {...common}
                        figId={f.fig_id}
                        page={f.page}
                        caption={f.caption}
                        sourceHash={f.source_hash ?? null}
                        externalSourceSlug={f.external_source_slug ?? null}
                      />
                    );
                  }
                  if (k === "table") {
                    const t = s as TableSource;
                    return (
                      <TableSourceCard
                        key={sourceKey(s, i)}
                        {...common}
                        page={t.page}
                        caption={t.caption}
                        summary={t.summary}
                        htmlPreview={t.html_preview}
                      />
                    );
                  }
                  if (k === "external") {
                    const ex = s as ExternalSource;
                    const { documentId: _drop, ...commonNoDoc } = common;
                    return (
                      <ExternalSourceCard
                        key={sourceKey(s, i)}
                        {...commonNoDoc}
                        content={ex.content}
                      />
                    );
                  }
                  const tx = s as TextSource;
                  return (
                    <TextSourceCard
                      key={sourceKey(s, i)}
                      {...common}
                      content={tx.content}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <button
        onClick={handleCopy}
        title={copied ? "Kopiert!" : "Antwort kopieren"}
        className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
